import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { useCreateTransaction } from '@/hooks/api'
import { addTransactionSchema, getDefaultValues } from './AddTransactionDialog.static'
import type { AddTransactionFormValues } from './AddTransactionDialog.static'
import type { ApiError } from '@/types'

interface UseAddTransactionDialogOptions {
  onClose: () => void
  patientId?: string
}

export function useAddTransactionDialog({ onClose, patientId }: UseAddTransactionDialogOptions) {
  const { t } = useTranslation('financials')
  const { t: tCommon } = useTranslation('common')

  const form = useForm<AddTransactionFormValues>({
    resolver: zodResolver(addTransactionSchema),
    defaultValues: getDefaultValues(patientId),
    mode: 'onBlur',
  })

  const createMutation = useCreateTransaction()
  const isLoading = createMutation.isPending

  const onSubmit = (data: AddTransactionFormValues) => {
    const amountCents = Math.round(Number(data.amount) * 100)

    createMutation.mutate(
      {
        transactionType: data.transactionType,
        category: data.category,
        description: data.description,
        amountCents,
        currency: 'EUR',
        transactionDate: data.transactionDate,
        patientId: data.patientId || null,
        paymentMethod: data.paymentMethod,
        referenceNumber: data.referenceNumber || null,
      },
      {
        onSuccess: () => {
          form.reset(getDefaultValues(patientId))
          onClose()
        },
        onError: (error) => {
          const axiosError = error as AxiosError<ApiError>
          const message = axiosError.response?.data?.error?.message || t('form.errors.createFailed')
          form.setError('root', { type: 'manual', message })
        },
      }
    )
  }

  return {
    t,
    tCommon,
    form,
    isLoading,
    onSubmit,
    hasPatientId: !!patientId,
  }
}
